import { useState, useRef, useCallback } from 'react';
import { useI18n } from '../../../../i18n';
import { BASE, getToken } from '../../../shared/services/api';

// ─── Tipos SSE del endpoint /via/chat/stream ────────────────────────────────

interface StreamSource {
  codigo: string;
  titulo: string;
  seccion?: string;
  similitud: number;
}

export interface SentinelFlagUI {
  ruleId: string;
  ruleName: string;
  category: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  message: string;
}

export interface AgentStepUI {
  node: string;
  label: string;
  iteration: number;
}

interface UseStreamChatOptions {
  contexto?: { modulo: string; vista: string; entidadId?: string; extra?: string };
}

interface UseStreamChatReturn {
  respuestaParcial: string;
  isStreaming: boolean;
  isThinking: boolean;
  error: string | null;
  fuentes: StreamSource[];
  conversacionId: string | null;
  proveedor: string | null;
  modelo: string | null;
  embeddingProvider: string | null;
  isAgent: boolean;
  agentStep: AgentStepUI | null;
  sentinelFlags: SentinelFlagUI[];
  enviar: (mensaje: string, historialId?: string) => void;
  cancelar: () => void;
}

/**
 * Hook para consumir el endpoint SSE de chat V·IA con streaming token-a-token.
 * Usa fetch + ReadableStream (no EventSource, porque necesitamos POST).
 */
export function useStreamChat(opts?: UseStreamChatOptions): UseStreamChatReturn {
  const { t } = useI18n();
  const chatI18n = t.via.chat as Record<string, string>;
  const [respuestaParcial, setRespuestaParcial] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fuentes, setFuentes] = useState<StreamSource[]>([]);
  const [conversacionId, setConversacionId] = useState<string | null>(null);
  const [proveedor, setProveedor] = useState<string | null>(null);
  const [modelo, setModelo] = useState<string | null>(null);
  const [embeddingProvider, setEmbeddingProvider] = useState<string | null>(null);
  const [isAgent, setIsAgent] = useState(false);
  const [agentStep, setAgentStep] = useState<AgentStepUI | null>(null);
  const [sentinelFlags, setSentinelFlags] = useState<SentinelFlagUI[]>([]);

  const abortRef = useRef<AbortController | null>(null);
  const chunkBufferRef = useRef('');
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 40ms (~25fps) solo para ollama local (50+ tok/s). Otros proveedores cloud ya
  // llegan a cadencia natural — flush inmediato para no añadir latencia artificial.
  const throttleMsRef = useRef<number>(0);

  const flushBuffer = useCallback(() => {
    if (chunkBufferRef.current) {
      const pending = chunkBufferRef.current;
      chunkBufferRef.current = '';
      setRespuestaParcial((prev) => prev + pending);
    }
    throttleTimerRef.current = null;
  }, []);

  const appendChunk = useCallback(
    (chunk: string) => {
      chunkBufferRef.current += chunk;
      if (throttleMsRef.current <= 0) {
        flushBuffer();
        return;
      }
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(flushBuffer, throttleMsRef.current);
      }
    },
    [flushBuffer],
  );

  const cancelar = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current);
      throttleTimerRef.current = null;
    }
    chunkBufferRef.current = '';
    setIsStreaming(false);
    setIsThinking(false);
  }, []);

  const enviar = useCallback(
    (mensaje: string, historialId?: string) => {
      // Cancelar stream previo si hay uno activo
      abortRef.current?.abort();
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
      chunkBufferRef.current = '';

      const controller = new AbortController();
      abortRef.current = controller;

      setRespuestaParcial('');
      setError(null);
      setFuentes([]);
      setIsAgent(false);
      setAgentStep(null);
      setSentinelFlags([]);
      setIsThinking(true);
      setIsStreaming(true);

      const body = JSON.stringify({
        mensaje,
        conversacionId: historialId,
        contexto: opts?.contexto,
      });

      // SSE stream: fetch manual con Bearer (no se puede usar request(), forzaría
      // consumo de .json() y rompería el reader). Token desde localStorage para
      // cross-subdomain Vercel+Railway (ver .claude/rules/auth.md).
      const token = getToken();
      fetch(`${BASE}/via/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body,
        signal: controller.signal,
      })
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => chatI18n.errorRed ?? 'Error de red');
            throw new Error(text);
          }

          const reader = res.body?.getReader();
          if (!reader) throw new Error('Sin cuerpo de respuesta');

          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            let currentEvent = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                const jsonStr = line.slice(6);
                try {
                  const data = JSON.parse(jsonStr);
                  switch (currentEvent) {
                    case 'start':
                      if (!data.isAgent) setIsThinking(false);
                      if (data.isAgent) setIsAgent(true);
                      if (data.conversacionId) setConversacionId(data.conversacionId as string);
                      if (data.proveedor) {
                        const tipo = String(data.proveedor).toLowerCase();
                        setProveedor(data.proveedor as string);
                        throttleMsRef.current = tipo === 'ollama' ? 40 : 0;
                      }
                      if (data.modelo) setModelo(data.modelo as string);
                      if (data.embeddingProvider)
                        setEmbeddingProvider(data.embeddingProvider as string);
                      break;
                    case 'agent_step':
                      setIsThinking(false);
                      setAgentStep(data as unknown as AgentStepUI);
                      break;
                    case 'sentinel':
                      if (data.flags) setSentinelFlags(data.flags as SentinelFlagUI[]);
                      break;
                    case 'chunk':
                      setIsThinking(false);
                      if (data.text) {
                        appendChunk(data.text as string);
                      }
                      break;
                    case 'sources':
                      if (data.documentos) setFuentes(data.documentos);
                      break;
                    case 'done':
                      if (data.conversacionId) setConversacionId(data.conversacionId as string);
                      if (data.proveedor) setProveedor(data.proveedor as string);
                      break;
                    case 'error':
                      setError(data.mensaje ?? chatI18n.errorServidor ?? 'Error del servidor');
                      break;
                  }
                } catch {
                  // JSON mal formado, ignorar
                }
                currentEvent = '';
              }
            }
          }
        })
        .catch((err: unknown) => {
          if (err instanceof Error && err.name === 'AbortError') return;
          setError(
            err instanceof Error ? err.message : (chatI18n.errorConexion ?? 'Error de conexion'),
          );
        })
        .finally(() => {
          if (throttleTimerRef.current) {
            clearTimeout(throttleTimerRef.current);
            throttleTimerRef.current = null;
          }
          flushBuffer();
          setIsStreaming(false);
          setIsThinking(false);
          abortRef.current = null;
        });
    },
    [
      opts?.contexto,
      appendChunk,
      flushBuffer,
      chatI18n.errorRed,
      chatI18n.errorServidor,
      chatI18n.errorConexion,
    ],
  );

  return {
    respuestaParcial,
    isStreaming,
    isThinking,
    error,
    fuentes,
    conversacionId,
    proveedor,
    modelo,
    embeddingProvider,
    isAgent,
    agentStep,
    sentinelFlags,
    enviar,
    cancelar,
  };
}
